import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useRegisterSW } from "virtual:pwa-register/react";
import { isAndroidPlatform } from "@/utils/platform";

export interface PwaUpdateContextValue {
  needRefresh: boolean;
  isChecking: boolean;
  registration: ServiceWorkerRegistration | null;
  checkForUpdate: (manual?: boolean) => Promise<{ hasUpdate: boolean; error?: string }>;
  updateApp: () => Promise<void>;
  dismissUpdate: () => void;
}

const PwaUpdateContext = createContext<PwaUpdateContextValue | null>(null);

const THROTTLE_MS = 5 * 60 * 1000; // 5 minutes between automatic background checks
const HOURLY_INTERVAL_MS = 60 * 60 * 1000; // 60 minutes fallback interval

export function PwaUpdateProvider({ children }: { children: React.ReactNode }) {
  const isAndroid = isAndroidPlatform();
  const location = useLocation();

  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const lastCheckRef = useRef<number>(Date.now());

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      if (r && !isAndroid) {
        setRegistration(r);
      }
    },
    onRegisterError(error) {
      console.error("SW registration error", error);
    },
  });

  // Core update check logic
  const checkForUpdate = useCallback(
    async (manual = false): Promise<{ hasUpdate: boolean; error?: string }> => {
      if (isAndroid) {
        return { hasUpdate: false };
      }

      if (!("serviceWorker" in navigator)) {
        return { hasUpdate: false, error: "Service workers are not supported in this browser." };
      }

      // If already pending refresh, report update available immediately
      if (needRefresh) {
        return { hasUpdate: true };
      }

      // For background triggers, throttle repeated network requests
      const now = Date.now();
      if (!manual && now - lastCheckRef.current < THROTTLE_MS) {
        return { hasUpdate: false };
      }
      lastCheckRef.current = now;

      if (manual) {
        setIsChecking(true);
      }

      try {
        let activeRegistration = registration;
        if (!activeRegistration) {
          activeRegistration = (await navigator.serviceWorker.getRegistration()) || null;
          if (activeRegistration) {
            setRegistration(activeRegistration);
          }
        }

        if (!activeRegistration) {
          return { hasUpdate: false };
        }

        // Trigger the service worker update check
        await activeRegistration.update();

        // Check if an update was found and placed into waiting or installing
        const updateFound = Boolean(activeRegistration.installing || activeRegistration.waiting);
        if (updateFound) {
          setNeedRefresh(true);
        }

        return { hasUpdate: updateFound || needRefresh };
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Failed to check for updates.";
        return { hasUpdate: false, error: message };
      } finally {
        if (manual) {
          setIsChecking(false);
        }
      }
    },
    [isAndroid, needRefresh, registration, setNeedRefresh]
  );

  // Trigger 1: Tab Visibility and Window Focus
  useEffect(() => {
    if (isAndroid) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForUpdate(false);
      }
    };

    const handleFocus = () => {
      checkForUpdate(false);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [isAndroid, checkForUpdate]);

  // Trigger 2: Route Navigation (e.g. returning to /library or changing views)
  useEffect(() => {
    if (isAndroid) return;
    checkForUpdate(false);
  }, [location.pathname, isAndroid, checkForUpdate]);

  // Hourly background timer fallback
  useEffect(() => {
    if (isAndroid) return;

    const timer = setInterval(() => {
      checkForUpdate(false);
    }, HOURLY_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [isAndroid, checkForUpdate]);

  const updateApp = useCallback(async () => {
    if (updateServiceWorker) {
      await updateServiceWorker(true);
    } else {
      window.location.reload();
    }
  }, [updateServiceWorker]);

  const dismissUpdate = useCallback(() => {
    setNeedRefresh(false);
  }, [setNeedRefresh]);

  const value: PwaUpdateContextValue = {
    needRefresh: isAndroid ? false : needRefresh,
    isChecking,
    registration,
    checkForUpdate,
    updateApp,
    dismissUpdate,
  };

  return <PwaUpdateContext.Provider value={value}>{children}</PwaUpdateContext.Provider>;
}

export function usePwaUpdate(): PwaUpdateContextValue {
  const context = useContext(PwaUpdateContext);
  if (!context) {
    return {
      needRefresh: false,
      isChecking: false,
      registration: null,
      checkForUpdate: async () => ({ hasUpdate: false }),
      updateApp: async () => {},
      dismissUpdate: () => {},
    };
  }
  return context;
}
